import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Info from "@mui/icons-material/Info";
import withAuth from "../../../../../../../Hoc/withAuth"; // Adjust path as needed
import AlertPopup from "../../../../../../../Components/Bits/AlertPopup"; // Adjust path as needed
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import {
  Stack,
  TextField,
  CardHeader,
  FormControl,
  FormLabel,
  FormControlLabel,
  Tooltip,
  Radio,
  RadioGroup,
  Button,
  LinearProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Grid,
  Box,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  WarningAmber as WarningAmberIcon,
  ErrorOutline as ErrorOutlineIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from "@mui/icons-material";

// ========================================================================
// HELPER FUNCTIONS
// ========================================================================

// --- Helper function to determine if a loaded answer means 'answered' ---
const isAnswerConsideredAnswered = (answer) => {
  if (!answer) return false;

  const hasValue =
    (answer.answer_value !== null &&
      answer.answer_value !== undefined &&
      String(answer.answer_value).trim() !== "") ||
    (answer.text_field_input !== null &&
      answer.text_field_input?.trim() !== "") ||
    (answer.yes_no_value !== null && answer.yes_no_value !== undefined);

  let dataCaptureAnswered = false;
  const requiresDataCapture =
    answer.answer_value !== null && Number(answer.answer_value) < 50;

  if (requiresDataCapture) {
    if (
      Array.isArray(answer.data_capture_values) &&
      answer.data_capture_values.length > 0
    ) {
      const firstCapture = answer.data_capture_values[0];
      dataCaptureAnswered = !!(
        firstCapture &&
        firstCapture.fieldName?.trim() &&
        firstCapture.from?.trim() &&
        firstCapture.to?.trim()
      );
    } else {
      dataCaptureAnswered = false; // Requires capture but has no data yet
    }
  } else if (answer.answer_value !== null) {
    // If data capture not required, it's answered if *any* value exists for this question type
    dataCaptureAnswered = true;
  }

  // A question is answered if it has a direct value OR if its data capture logic determines it's answered
  return hasValue || dataCaptureAnswered;
};

const processQuestions = (initialQuestionGroups, results) => {
  if (
    !initialQuestionGroups ||
    !Array.isArray(initialQuestionGroups) ||
    initialQuestionGroups.length === 0
  ) {
    console.warn("processQuestions called with invalid initialQuestionGroups");
    return initialQuestionGroups || [];
  }
  const isResultsArray = Array.isArray(results);
  if (!isResultsArray) {
    console.warn(
      "processQuestions received 'results' which is not an array. Value:",
      results,
    );

    return initialQuestionGroups.map((group) => ({
      ...group,
      question: group.question.map((question) => ({
        ...question,
        answer: {
          comments: "",
          answer_value: null,
          text_field_input: "",
          yes_no_value: null,
          data_capture_values: null,
          answered: false,
          auto_fail: false,
          sendEmail: false,
        },
      })),
    }));
  }

  return initialQuestionGroups.map((group) => {
    const updatedQuestions = group.question.map((question) => {
      // Find the corresponding result using the (now guaranteed) array method
      const result = results.find((r) => r && r.question_id === question.id); // Added check for r existing

      if (result) {
        const loadedAnswer = {
          comments: result.comments ?? "",
          answer_value: result.answer_value ?? null,
          text_field_input: result.text_field_input ?? "",
          data_capture_values: Array.isArray(result.data_capture_values)
            ? result.data_capture_values
            : null,
          yes_no_value: result.yes_no_value ?? null,
          auto_fail: result.auto_fail ?? false,
          sendEmail: result.sendEmail ?? false,
          answered: false, // Will be recalculated
        };
        loadedAnswer.answered = isAnswerConsideredAnswered(loadedAnswer);

        return { ...question, answer: loadedAnswer };
      }
      // If no result, return with default answer structure
      return {
        ...question,
        answer: {
          comments: "",
          answer_value: null,
          text_field_input: "",
          yes_no_value: null,
          data_capture_values: null,
          answered: false,
          auto_fail: false,
          sendEmail: false,
        },
      };
    });
    return { ...group, question: updatedQuestions };
  });
};

// ========================================================================
// MAIN REINITIALIZE COMPONENT
// ========================================================================
const Reinitialize = ({
  callListeningTime = 0,
  user,
  setStart,
  endEvaluation, // MUST be the object returned by useMutation in the parent { mutate, isLoading, ... }
  getCurrentTime,
}) => {
  const { id, evalID } = useParams();

  // --- Ref to store the latest listening time without causing re-renders ---
  const latestCallListeningTimeRef = useRef(callListeningTime);

  // --- Effect to update the Ref when the prop changes ---
  useEffect(() => {
    // Update the ref's current value whenever the prop changes.
    // This does NOT trigger a re-render of Reinitialize.
    latestCallListeningTimeRef.current = callListeningTime;
    // We don't need to do anything else here.
  }, [callListeningTime]); // Only run when the specific prop changes

  const UserEmail = user?.email;

  const [allQuestions, setAllQuestions] = useState([]);
  const [isProcessing, setIsProcessing] = useState(true); // Tracks initial data loading and merging
  const [complete, setComplete] = useState(false);
  const [showAlert, setShowAlert] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [dialogOpen, setDialogOpen] = useState(false);

  // --- React Query Config ---
  const config = React.useMemo(
    () => ({
      headers: { Authorization: `Bearer ${user?.token}` },
    }),
    [user?.token],
  );

  // --- Fetch Evaluation Metadata (e.g., old time) ---
  const { data: evaluationData, isLoading: isLoadingEvalDetails } = useQuery({
    queryKey: ["getEvaluation", evalID],
    queryFn: async () => {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/QAInbound/admin/Evaluations/${id}/Evaluations/${evalID}`,
        config,
      );

      return res.data?.data; // Adjust based on API response
    },
    enabled: !!id && !!evalID && !!config.headers.Authorization,
    staleTime: 5 * 60 * 1000, // Cache for 5 mins
    refetchOnWindowFocus: false,
  });

  const oldtime = evaluationData?.call_listening_time || 0;

  // --- Fetch Question Structure ---
  const {
    data: questionGroupsData,
    isLoading: isLoadingGroups,
    isError: isErrorGroups,
  } = useQuery({
    queryKey: ["getActiveQuestionGroups", id],
    queryFn: async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/QAInbound/admin/${id}/group/?active=true&campaignId=${id}`,
        config,
      );
      return response?.data?.data; // Expecting array of groups
    },
    enabled: !!id && !!config.headers.Authorization,
    staleTime: 15 * 60 * 1000, // Cache structure longer
    refetchOnWindowFocus: false,
  });

  // --- Fetch Existing Evaluation Results ---
  const {
    data: resultsData,
    isLoading: isLoadingResults,
    isError: isErrorResults,
  } = useQuery({
    queryKey: ["GetEvaluationHistory", evalID],
    queryFn: async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/QAInbound/admin/results/${evalID}/results`,
          config,
        );
        const data = response?.data?.data;

        if (Array.isArray(data)) {
          return data;
        } else {
          console.warn(
            `API results for ${evalID} did not return an array. Received:`,
            data,
          );
          return [];
        }
      } catch (error) {
        console.error(`Error fetching results for ${evalID}:`, error);
        return [];
      }
    },
    enabled:
      !!evalID &&
      !!config.headers.Authorization &&
      !isLoadingGroups &&
      !!questionGroupsData,
    staleTime: 1 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // --- Send Email Mutation ---
  // const SendEmail = useMutation({
  //   mutationFn: async ({ targetEvalID, campaignID }) => {
  //     await axios.post(
  //       `${process.env.REACT_APP_API_URL}/QaInbound/admin/evaluations/${campaignID}/${targetEvalID}/sendemail`,
  //       {},
  //       config
  //     );
  //   },
  //   onError: (error) => {
  //     console.error("Failed to send email:", error);
  //     // Optional: Show non-blocking alert
  //     // setShowAlert({ open: true, message: "Evaluation submitted, but failed to send email notification.", severity: "warning" });
  //   },
  // });

  // --- Effect to Merge Question Structure and Results ---
  useEffect(() => {
    // Start processing when any relevant data starts loading
    if (isLoadingGroups || isLoadingResults || isLoadingEvalDetails) {
      setIsProcessing(true);
    }

    // Proceed only when all loading is finished and we have the question structure
    if (
      !isLoadingGroups &&
      !isLoadingResults &&
      !isLoadingEvalDetails &&
      questionGroupsData
    ) {
      // 1. Initialize base structure with default answers
      const initialQuestions = questionGroupsData
        .map((group) => ({
          ...group,
          question: group.question.map((question) => ({
            ...question,
            answer: {
              comments: "",
              answer_value: null,
              text_field_input: "",
              yes_no_value: null,
              data_capture_values: null,
              answered: false,
              auto_fail: false,
              sendEmail: false,
            },
          })),
        }))
        .sort((a, b) => a.order - b.order);

      // 2. Merge existing results (resultsData is guaranteed array)
      const mergedQuestions = processQuestions(initialQuestions, resultsData);

      // 3. Set the final state
      setAllQuestions(mergedQuestions);
      setIsProcessing(false); // Finish processing
    } else if (
      !isLoadingGroups &&
      !isLoadingResults &&
      !isLoadingEvalDetails &&
      !questionGroupsData &&
      !isErrorGroups
    ) {
      // Handle case where loading finished but no groups found (and no error reported)
      console.warn("No question groups found for this configuration.");
      setAllQuestions([]);
      setIsProcessing(false);
    } else if (
      !isLoadingGroups &&
      !isLoadingResults &&
      !isLoadingEvalDetails &&
      (isErrorGroups || isErrorResults)
    ) {
      // Handle case where loading finished but there was an error fetching data
      console.error("Error loading question groups or results.");
      setAllQuestions([]); // Set empty state on error
      setIsProcessing(false);
    }
  }, [
    questionGroupsData,
    resultsData,
    evaluationData, // Data dependencies
    isLoadingGroups,
    isLoadingResults,
    isLoadingEvalDetails, // Loading state dependencies
    isErrorGroups,
    isErrorResults, // Error state dependencies
  ]);

  // --- Effect to Calculate Overall Completeness ---
  useEffect(() => {
    if (!isProcessing && allQuestions && allQuestions.length > 0) {
      const isComplete = allQuestions.every((group) =>
        group.question.every((question) => question.answer.answered),
      );
      setComplete(isComplete);
    } else {
      setComplete(false);
    }
  }, [allQuestions, isProcessing]);

  const prepareSaveData = useCallback(() => {
    const currentListeningTime = getCurrentTime ? getCurrentTime() : 0;
    const totalTime = currentListeningTime + (oldtime || 0);

    return {
      evaluation_id: evalID,
      email: UserEmail,
      call_listening_time: totalTime,
      results: allQuestions.map((group) => ({
        id: group.id,
        name: group.name,
        order: group.order,
        question: group.question,
      })),
    };
  }, [allQuestions, evalID, UserEmail, oldtime, getCurrentTime]);

  const handleSaveProgress = useCallback(() => {
    if (!endEvaluation || typeof endEvaluation.mutate !== "function") {
      console.error("endEvaluation mutate function is not available.");
      setShowAlert({
        open: true,
        message: "Error: Cannot save evaluation.",
        severity: "error",
      });
      return;
    }
    const dataToSave = prepareSaveData();
    console.log(
      "Saving Progress Payload:",
      JSON.stringify(dataToSave, null, 2),
    );

    endEvaluation.mutate(dataToSave, {
      onSuccess: () => {
        setShowAlert({
          open: true,
          message: "Progress saved successfully",
          severity: "success",
        });
        setTimeout(() => {
          setShowAlert((prev) => ({ ...prev, open: false }));

          if (setStart) setStart(false);
        }, 1500);
      },
      onError: (error) => {
        console.error(
          "Failed to save progress:",
          error.response?.data || error.message,
        );
        setShowAlert({
          open: true,
          message: `Failed to save progress: ${
            error.response?.data?.message || error.message || "Unknown error"
          }`,
          severity: "error",
        });
      },
    });
  }, [prepareSaveData, endEvaluation, setStart]);

  // --- Handler to Complete and Submit Evaluation ---
  const handleCompleteEvaluation = useCallback(() => {
    if (!endEvaluation || typeof endEvaluation.mutate !== "function") {
      console.error("endEvaluation mutate function is not available.");
      setShowAlert({
        open: true,
        message: "Error: Cannot submit evaluation.",
        severity: "error",
      });
      return;
    }
    const dataToSubmit = prepareSaveData();
    console.log(
      "Submitting Evaluation Payload:",
      JSON.stringify(dataToSubmit, null, 2),
    ); // Detailed log

    endEvaluation.mutate(dataToSubmit, {
      onSuccess: () => {
        if (setStart) setStart(false);
        setShowAlert({
          open: true,
          message: "Evaluation submitted successfully!",
          severity: "success",
        });
        setTimeout(() => {
          setShowAlert((prev) => ({ ...prev, open: false }));
        }, 1500);
      },
      onError: (error) => {
        console.error(
          "Failed to submit evaluation:",
          error.response?.data || error.message,
        );
        setShowAlert({
          open: true,
          message: `Failed to submit evaluation: ${
            error.response?.data?.message || error.message || "Unknown error"
          }`,
          severity: "error",
        });
      },
    });
  }, [prepareSaveData, endEvaluation, setStart]);

  // --- Handler to Get Failed Questions for Dialog ---

  const getFailedQuestions = useCallback(() => {
    const failed = [];
    if (!allQuestions) return failed;

    allQuestions.forEach((group) => {
      group.question.forEach((question) => {
        const answer = question.answer;

        let isFail = false;
        let failReason = "";

        if (
          question.question_type === "Yes/No" &&
          answer.answer_value === "0"
        ) {
          isFail = true;
          failReason = "Answered 'No'";
        } else if (
          question.question_type === "Score" &&
          answer.answer_value !== null &&
          Number(answer.answer_value) < 3
        ) {
          isFail = true;
          failReason = `Score: ${answer.answer_value} (Below Threshold)`;
        } else if (
          question.question_type === "Data Capture" &&
          answer.answer_value !== null &&
          Number(answer.answer_value) < 50
        ) {
          isFail = true;
          failReason = `Failed Data Capture Requirement (Score: ${answer.answer_value})`;
        } else if (answer.answer_value === "0") {
          isFail = true;
          failReason = "Auto-Fail Condition Met";
        }
        // Add other failure conditions if needed (e.g., required text field empty)

        if (isFail) {
          failed.push({ question: question.name, answer: failReason });
        }
      });
    });
    return failed;
  }, [allQuestions]);

  const getUnansweredQuestions = () => {
    const unanswered = [];
    if (!allQuestions) return unanswered;

    allQuestions.forEach((group) => {
      group.question.forEach((question) => {
        const answer = question.answer;
        if (!answer.answered) {
          unanswered.push({ question: question.name, answer: "Not Answered" });
        }
      });
    });
    return unanswered;
  };

  const navigateToFirstUnanswered = useCallback(() => {
    if (!allQuestions) return;

    let firstUnansweredId = null;
    for (const group of allQuestions) {
      for (const question of group.question) {
        if (!question.answer?.answered) {
          firstUnansweredId = `question-${question.id}`;
          break;
        }
      }
      if (firstUnansweredId) {
        break;
      }
    }

    if (firstUnansweredId) {
      const element = document.getElementById(firstUnansweredId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus({ preventScroll: true });
        element.style.transition = "outline 0.1s ease-in-out";
        element.style.outline = "2px solid blue";
        setTimeout(() => {
          element.style.outline = "none";
        }, 1500);
      } else {
        console.warn("Could not find element with ID:", firstUnansweredId);
      }
    } else {
      setShowAlert({
        open: true,
        message: "All questions have been answered.",
        severity: "info",
      });
      setTimeout(
        () => setShowAlert((prev) => ({ ...prev, open: false })),
        1500,
      );
    }
  }, [allQuestions]);

  // --- Dialog Handlers ---
  const handleDialogClose = () => setDialogOpen(false);
  const handleDialogConfirm = () => {
    handleCompleteEvaluation(); // Trigger submission
    setDialogOpen(false);
  };

  // --- Render Loading State ---
  if (
    isLoadingGroups ||
    isLoadingResults ||
    isLoadingEvalDetails ||
    isProcessing
  ) {
    return (
      <LinearProgress
        sx={{ position: "absolute", top: 0, left: 0, right: 0 }}
      />
    );
  }

  // --- Render Error State ---
  if (isErrorGroups || isErrorResults) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="error">
          <AlertTitle>Error Loading Data</AlertTitle>
          Failed to load evaluation questions or results. Please try refreshing
          the page or contact support if the problem persists.
          {isErrorGroups && (
            <Typography variant="body2" component="p" sx={{ mt: 1 }}>
              - Could not load question structure.
            </Typography>
          )}
          {isErrorResults && (
            <Typography variant="body2" component="p" sx={{ mt: 1 }}>
              - Could not load previous results.
            </Typography>
          )}
        </Alert>
        <Button
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
          variant="outlined"
        >
          Refresh Page
        </Button>
      </Box>
    );
  }

  // --- Render Main Content ---
  return (
    <Box pb={24}>
      {allQuestions && allQuestions.length > 0 ? (
        <>
          {allQuestions.map((group) => {
            return (
              <Stack key={group.id} sx={{ mt: 2, mb: 2 }}>
                <Card elevation={1}>
                  <CardHeader
                    titleTypographyProps={{
                      variant: "h6",
                      gutterBottom: false,
                    }}
                    subheaderTypographyProps={{
                      variant: "body2",
                      color: "text.secondary",
                    }}
                    title={group.name}
                    subheader={group.group_type}
                    sx={{ pb: 1 }}
                  />
                  <CardContent sx={{ pt: 0 }}>
                    {group.question.map((question) => {
                      const QuestionWrapper = ({ children }) => (
                        <Box
                          id={`question-${question.id}`}
                          sx={{
                            py: 1.5,
                            "&:focus-within": {
                              outline: "primary.main",
                              outlineOffset: "2px",
                              borderRadius: "4px",
                            },
                          }}
                          tabIndex={-1}
                        >
                          {children}
                        </Box>
                      );

                      switch (question.question_type) {
                        case "Yes/No":
                          return (
                            <QuestionWrapper key={question.id}>
                              <YesNoQuestion
                                key={question.id}
                                question={question}
                                handleSetValues={setAllQuestions}
                              />
                            </QuestionWrapper>
                          );
                        case "Text Input":
                          return (
                            <QuestionWrapper key={question.id}>
                              <TextQuestion
                                key={question.id}
                                question={question}
                                handleSetValues={setAllQuestions}
                              />
                            </QuestionWrapper>
                          );
                        case "Data Capture":
                          return (
                            <QuestionWrapper key={question.id}>
                              <DataCaptureQuestion
                                key={question.id}
                                question={question}
                                handleSetValues={setAllQuestions}
                              />
                            </QuestionWrapper>
                          );
                        case "Score":
                          return (
                            <QuestionWrapper key={question.id}>
                              <ScoreQuestion
                                key={question.id}
                                question={question}
                                handleSetValues={setAllQuestions}
                              />
                            </QuestionWrapper>
                          );
                        default:
                          console.warn(
                            "Unknown question type:",
                            question.question_type,
                          );
                          return (
                            <Alert
                              severity="warning"
                              sx={{ mt: 1 }}
                              key={question.id}
                            >
                              Unsupported question type:{" "}
                              {question.question_type}
                            </Alert>
                          );
                      }
                    })}
                  </CardContent>
                </Card>
              </Stack>
            );
          })}
          {/* --- Fixed Bottom Save/Submit Buttons --- */}
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 2,
              px: 2,
              backgroundColor: "background.paper",
              boxShadow: "0 -2px 8px rgba(0,0,0,0.12)",
              zIndex: 1100, // Ensure above other content
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{
                width: "100%",

                ml: 7,
              }}
            >
              <Button
                color="inherit"
                size="small"
                sx={{ width: { xs: "50%", sm: "40%", md: "30%" } }}
                onClick={navigateToFirstUnanswered}
                disabled={
                  endEvaluation?.isLoading ||
                  // no more unanswered questions
                  getUnansweredQuestions().length === 0
                }
              >
                unanswered questions : {getUnansweredQuestions().length}
              </Button>
              {complete ? (
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ width: { xs: "90%", sm: "60%", md: "50%" } }}
                  onClick={() => setDialogOpen(true)}
                  disabled={endEvaluation?.isLoading} // Check isLoading from prop
                >
                  {endEvaluation?.isLoading
                    ? "Submitting..."
                    : "Submit Evaluation"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ width: { xs: "90%", sm: "60%", md: "50%" } }}
                  onClick={handleSaveProgress}
                  disabled={endEvaluation?.isLoading}
                >
                  {endEvaluation?.isLoading ? "Saving..." : "Save Progress"}
                </Button>
              )}
            </Stack>
          </Box>
        </>
      ) : (
        // Display message if loading finished but no questions found
        <Box sx={{ padding: 3, textAlign: "center", mt: 4 }}>
          <Alert severity="info" icon={<Info fontSize="inherit" />}>
            No evaluation questions are currently configured or active for this
            campaign.
          </Alert>
        </Box>
      )}
      {/* --- Alert Popup for Notifications --- */}
      <AlertPopup
        open={showAlert.open}
        message={showAlert.message}
        severity={showAlert.severity}
        onClose={() => setShowAlert((prev) => ({ ...prev, open: false }))}
      />
      {/* --- Confirmation Dialog before Submit --- */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        aria-labelledby="confirm-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          id="confirm-dialog-title"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          {getFailedQuestions().length > 0 ? (
            <WarningAmberIcon color="warning" />
          ) : (
            <CheckCircleOutlineIcon color="success" />
          )}
          Confirm Submission
        </DialogTitle>
        <DialogContent dividers sx={{ pb: 3 }}>
          {getFailedQuestions().length > 0 ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Failing Answers</AlertTitle>
                Some answers do not meet the passing criteria. Please review
              </Alert>
              <List
                dense
                disablePadding
                sx={{ maxHeight: "300px", overflow: "auto" }}
              >
                {" "}
                {/* Scrollable list */}
                {getFailedQuestions().map((item, index) => (
                  <React.Fragment key={`${item.question}-${index}`}>
                    {" "}
                    {/* More robust key */}
                    <ListItem
                      alignItems="flex-start"
                      justifyContent="center"
                      sx={{ py: 0.5 }}
                    >
                      <ListItemIcon sx={{ mt: 1, minWidth: 36 }}>
                        <ErrorOutlineIcon color="error" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            component="span"
                            sx={{ fontWeight: 500 }}
                          >
                            {item.question}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            component="span"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            {item.answer}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {index < getFailedQuestions().length - 1 && (
                      <Divider variant="inset" component="li" />
                    )}
                  </React.Fragment>
                ))}
              </List>
              <Typography
                variant="body2"
                color="text.secondary"
                mt={3}
                textAlign="center"
              >
                Do you want to proceed with submitting the evaluation?
              </Typography>
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                py: 3,
              }}
            >
              <CheckCircleOutlineIcon
                color="success"
                sx={{ fontSize: 48, mb: 2 }}
              />
              <Typography variant="h6" gutterBottom>
                Ready to Submit
              </Typography>
              <Typography variant="body1" color="text.secondary">
                All answers appear to meet the passing criteria.
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={3}>
                Confirm final submission?
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {" "}
          {/* Added padding */}
          <Button onClick={handleDialogClose} color="inherit" variant="text">
            {" "}
            {/* Text variant for cancel */}
            Cancel
          </Button>
          <Button
            onClick={handleDialogConfirm}
            color={getFailedQuestions().length > 0 ? "warning" : "primary"}
            variant="contained"
            autoFocus
            disabled={endEvaluation?.isLoading} // Check isLoading from prop
          >
            {endEvaluation?.isLoading ? "Submitting..." : "Confirm Submission"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Apply HOC (Ensure it correctly passes props like `user`)
export default withAuth(Reinitialize);

// ========================================================================
// CHILD QUESTION COMPONENTS (Using Functional Updates for handleSetValues)
// ========================================================================

const YesNoQuestion = React.memo(({ question, handleSetValues }) => {
  // --- Local State for Comment ---
  const [localComment, setLocalComment] = useState(
    () => question?.answer?.comments ?? "",
  );

  // --- Effect to Sync Prop Changes TO Local State ---
  useEffect(() => {
    const propComment = question?.answer?.comments ?? "";
    if (propComment !== localComment) {
      console.log(
        `YesNoQuestion ${question.id}: Prop comment ('${propComment}') differs from local ('${localComment}'). Updating local state.`,
      );
      setLocalComment(propComment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.answer?.comments]); // Depend only on the comment prop

  // --- Radio Button Logic (Updates global state directly) ---
  const updateAnswer = useCallback(
    (newValue, newYesNoValue) => {
      handleSetValues((prevAllQuestions) =>
        prevAllQuestions.map((group) => ({
          ...group,
          question: group.question.map((q) =>
            q.id === question.id
              ? {
                  ...q,
                  answer: {
                    ...q.answer, // Preserve existing comment
                    yes_no_value: newYesNoValue,
                    answer_value: newValue, // This is '100' or '0'
                    answered: true, // Selecting Yes/No marks it answered
                  },
                }
              : q,
          ),
        })),
      );
    },
    [question.id, handleSetValues],
  );

  // Handler for Radio change
  const handleChange = (e) => {
    const inputValue = e.target.value; // '100' or '0'
    const yesNoValue =
      inputValue === "100" ? true : inputValue === "0" ? false : null;
    updateAnswer(inputValue, yesNoValue);
  };

  // --- Comment Logic (Uses Local State) ---

  // Handler for LOCAL comment change
  const handleLocalCommentChange = (e) => {
    setLocalComment(e.target.value); // Only update local state while typing
  };

  // Function to update GLOBAL state on Blur
  const syncGlobalComment = useCallback(() => {
    const currentGlobalComment = question?.answer?.comments ?? "";
    if (localComment !== currentGlobalComment) {
      console.log(
        `YesNoQuestion ${question.id}: Syncing global comment on blur:`,
        localComment,
      );
      handleSetValues((prevAllQuestions) =>
        prevAllQuestions.map((group) => ({
          ...group,
          question: group.question.map((q) =>
            q.id === question.id
              ? {
                  ...q,
                  answer: { ...q.answer, comments: localComment }, // Only update comment in global state
                }
              : q,
          ),
        })),
      );
    } else {
      console.log(
        `YesNoQuestion ${question.id}: Blur detected, but local comment matches prop. No global update needed.`,
      );
    }
  }, [localComment, question?.id, question?.answer?.comments, handleSetValues]);

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ pt: 1.5, pb: "12px !important" }}>
        <Stack spacing={1}>
          {/* --- Yes/No Radio Section --- */}
          <FormControl component="fieldset">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              mb={0.5}
            >
              {/* Label/Tooltip */}
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <FormLabel
                  id={`label-${question.id}`}
                  sx={{ fontWeight: 500, fontSize: "0.95rem" }}
                >
                  {question?.name}
                </FormLabel>
                {question.description && (
                  <Tooltip title={question.description} placement="top">
                    <Info
                      color="action"
                      sx={{
                        fontSize: "1rem",
                        cursor: "help",
                        verticalAlign: "middle",
                      }}
                    />
                  </Tooltip>
                )}
              </Stack>
              {/* Answered icon reflects GLOBAL state */}
              {question.answer.answered && (
                <DoneAllIcon color="success" fontSize="small" />
              )}
            </Stack>
            <RadioGroup
              value={question.answer.answer_value ?? ""} // Read global state for Yes/No value
              onChange={handleChange} // Update global state directly
              aria-labelledby={`label-${question.id}`}
              name={`radio-${question.id}`}
              row
            >
              <FormControlLabel
                value="100" // Corresponds to answer_value
                control={<Radio size="small" />}
                label="Yes"
                sx={{ mr: 2 }}
              />
              <FormControlLabel
                value="0" // Corresponds to answer_value
                control={<Radio size="small" />}
                label="No"
              />
              {
                // if question.not_applicable is true, show N/A option
                question.not_applicable && (
                  <FormControlLabel
                    value="N/A"
                    control={<Radio size="small" />}
                    label="N/A"
                  />
                )
              }
            </RadioGroup>
          </FormControl>

          {/* --- Comment Text Field Section --- */}
          <TextField
            value={localComment} // <--- Bound to local state
            onChange={handleLocalCommentChange} // <--- Updates local state
            onBlur={syncGlobalComment} // <--- Updates global state on blur
            variant="outlined"
            size="small"
            placeholder="Comments (Optional)"
            fullWidth
            multiline
            rows={2}
          />
        </Stack>
      </CardContent>
    </Card>
  );
});

const TextQuestion = React.memo(({ question, handleSetValues }) => {
  // Use optional chaining for safety during initial render
  const initialText = question?.answer?.text_field_input ?? "";
  const [localText, setLocalText] = useState(initialText);

  // Ref to track if the component has mounted
  const isMounted = useRef(false);

  // Effect to sync prop changes (like initial load) to local state
  useEffect(() => {
    const propText = question?.answer?.text_field_input ?? "";

    // After the initial mount, if the prop differs from local state, update local state.
    // This handles cases where the initial prop was null/undefined and then loads.
    // It avoids resetting user input if the prop changes due to unrelated re-renders.
    if (isMounted.current) {
      if (propText !== localText) {
        console.log(
          `TextQuestion ${question.id}: Prop updated, syncing local state. New prop:`,
          propText,
        );
        setLocalText(propText);
      }
    } else {
      // On the very first run of the effect (after mount), explicitly set state
      // if the prop value available NOW is different from the initial value useState got.
      // This covers the case where data arrived between initial render and this effect run.
      if (propText !== initialText) {
        console.log(
          `TextQuestion ${question.id}: Initial data loaded after mount, syncing local state. Prop:`,
          propText,
        );
        setLocalText(propText);
      }
      isMounted.current = true; // Mark as mounted
    }
    // We only want this effect to run when the SOURCE prop changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.answer?.text_field_input]); // Depend only on the relevant prop value

  // Handler for LOCAL text change
  const handleLocalTextChange = (e) => {
    setLocalText(e.target.value); // Update local state only
  };

  // Function to update GLOBAL state on Blur
  const syncGlobalText = useCallback(() => {
    const currentPropText = question?.answer?.text_field_input ?? "";
    // Only update global state if the local value has actually changed from what's currently in the global state
    if (localText !== currentPropText) {
      console.log(
        `TextQuestion ${question.id}: Syncing global text on blur:`,
        localText,
      );
      handleSetValues((prevAllQuestions) =>
        prevAllQuestions.map((group) => ({
          ...group,
          question: group.question.map((q) =>
            q.id === question.id
              ? {
                  ...q,
                  answer: {
                    ...(q.answer || {}), // Ensure answer object exists before spreading
                    text_field_input: localText, // Update global state text
                    answer_value: localText.trim(), // Ensure answer_value reflects text
                    answered: localText.trim() !== "", // Update answered based on new text
                  },
                }
              : q,
          ),
        })),
      );
    } else {
      console.log(
        `TextQuestion ${question.id}: Blur detected, but local text matches prop. No global update needed.`,
      );
    }
  }, [
    localText,
    question?.id,
    question?.answer?.text_field_input,
    handleSetValues,
  ]); // Added optional chaining for safety

  // Determine answered status based on the current local text for immediate UI feedback
  const isAnsweredLocally = localText.trim() !== "";

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ pt: 1.5, pb: "12px !important" }}>
        <Stack spacing={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            mb={0.5}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <FormLabel
                id={`label-${question.id}`}
                sx={{ fontWeight: 500, fontSize: "0.95rem" }}
              >
                {question?.name}
              </FormLabel>
              {question.description && (
                <Tooltip title={question.description} placement="top">
                  <Info
                    color="action"
                    sx={{
                      fontSize: "1rem",
                      cursor: "help",
                      verticalAlign: "middle",
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
            {/* Show answered icon based on local state for immediate feedback */}
            {isAnsweredLocally && (
              <DoneAllIcon color="success" fontSize="small" />
            )}
          </Stack>
          <TextField
            value={localText} // USE LOCAL STATE
            onChange={handleLocalTextChange} // USE LOCAL HANDLER
            onBlur={syncGlobalText} // Sync global state on blur
            variant="outlined"
            size="small"
            placeholder="Enter response"
            fullWidth
            multiline
            rows={3}
          />
        </Stack>
      </CardContent>
    </Card>
  );
});

const ScoreQuestion = React.memo(({ question, handleSetValues }) => {
  // --- Local State for Comment ---
  // Initialize with the comment from props available at mount time.
  const [localComment, setLocalComment] = useState(
    () => question?.answer?.comments ?? "",
  );

  // --- Effect to Sync Prop Changes TO Local State ---
  useEffect(() => {
    // Get the current value of the comment from the props.
    const propComment = question?.answer?.comments ?? "";

    // Compare the prop value with the current local state value.
    // If they are different, it means the prop has updated (e.g., data loaded)
    // and we need to update the local state to match.
    if (propComment !== localComment) {
      console.log(
        `ScoreQuestion ${question.id}: Prop comment ('${propComment}') differs from local ('${localComment}'). Updating local state.`,
      );
      setLocalComment(propComment);
    }
    // This effect should ONLY run when the specific prop 'question.answer.comments' changes.
    // It should NOT depend on 'localComment'.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.answer?.comments]); // Dependency array is crucial!

  // --- Radio Button Logic (Updates global state directly) ---
  const updateScore = useCallback(
    (score) => {
      handleSetValues((prevAllQuestions) =>
        prevAllQuestions.map((group) => ({
          ...group,
          question: group.question.map((q) =>
            q.id === question.id
              ? {
                  ...q,
                  // Make sure to preserve the existing comment when updating score
                  answer: { ...q.answer, answer_value: score, answered: true },
                }
              : q,
          ),
        })),
      );
    },
    [question.id, handleSetValues], // Depends on question.id and the setter function
  );

  // Handler for Radio change
  const handleChange = (e) => {
    updateScore(e.target.value);
  };

  // --- Comment Logic (Uses Local State) ---

  // Handler for LOCAL comment change
  const handleLocalCommentChange = (e) => {
    setLocalComment(e.target.value); // Only update the local state while typing
  };

  // Function to update GLOBAL state on Blur
  const syncGlobalComment = useCallback(() => {
    // Get the comment currently in the global state for comparison
    const currentGlobalComment = question?.answer?.comments ?? "";

    // Only push the update if the local state differs from the current global state
    if (localComment !== currentGlobalComment) {
      console.log(
        `ScoreQuestion ${question.id}: Syncing global comment on blur:`,
        localComment,
      );
      handleSetValues((prevAllQuestions) =>
        prevAllQuestions.map((group) => ({
          ...group,
          question: group.question.map((q) =>
            q.id === question.id
              ? {
                  ...q,
                  // Update the answer object in the global state
                  answer: { ...q.answer, comments: localComment },
                }
              : q,
          ),
        })),
      );
    } else {
      console.log(
        `ScoreQuestion ${question.id}: Blur detected, but local comment matches prop. No global update needed.`,
      );
    }
  }, [localComment, question?.id, question?.answer?.comments, handleSetValues]); // Dependencies for the sync function

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ pt: 1.5, pb: "12px !important" }}>
        <Stack spacing={1}>
          {/* --- Radio Button Section --- */}
          <FormControl component="fieldset">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              mb={0.5}
            >
              {/* FormLabel / Tooltip / DoneIcon... */}
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <FormLabel
                  id={`label-${question.id}`}
                  sx={{ fontWeight: 500, fontSize: "0.95rem" }}
                >
                  {question?.name}
                </FormLabel>
                {question.description && (
                  <Tooltip title={question.description} placement="top">
                    <Info
                      color="action"
                      sx={{
                        fontSize: "1rem",
                        cursor: "help",
                        verticalAlign: "middle",
                      }}
                    />
                  </Tooltip>
                )}
              </Stack>
              {/* Check answered status based on GLOBAL state here for radio */}
              {question.answer.answered && (
                <DoneAllIcon color="success" fontSize="small" />
              )}
            </Stack>
            <RadioGroup
              onChange={handleChange}
              aria-labelledby={`label-${question.id}`}
              value={question.answer.answer_value ?? ""} // Reads global state
              row
              name={`radio-${question.id}`}
              sx={{ flexWrap: "wrap", gap: "2px 8px" }}
            >
              {[
                { value: "0", label: "0=Very Poor" },
                { value: "1", label: "1=Poor" },
                { value: "2", label: "2=Fair" },
                { value: "3", label: "3=Good" },
                { value: "4", label: "4=Very Good" },
                { value: "5", label: "5=Excellent" },
              ].map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={<Radio size="small" />}
                  label={opt.label}
                  sx={{ mr: 1 }}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {/* --- Comment Text Field Section --- */}
          <TextField
            value={localComment} // <--- Bound to local state
            onChange={handleLocalCommentChange} // <--- Updates local state
            onBlur={syncGlobalComment} // <--- Updates global state on blur
            variant="outlined"
            size="small"
            placeholder="Comments (Optional)"
            fullWidth
            multiline
            rows={2}
          />
        </Stack>
      </CardContent>
    </Card>
  );
});

const DataCaptureQuestion = React.memo(({ question, handleSetValues }) => {
  // --- Local State for the Data Capture Fields ---
  const [localDataCaptureFields, setLocalDataCaptureFields] = useState(() => {
    // Initialize from prop data available at mount time
    const initialData = question?.answer?.data_capture_values;
    if (Array.isArray(initialData) && initialData.length > 0) {
      return { ...initialData[0] }; // Return a clone
    }
    return { fieldName: "", from: "", to: "" }; // Default empty
  });

  // --- Derived State/Effect for Visibility ---
  const [showDataCaptureBox, setShowDataCaptureBox] = useState(false);

  useEffect(() => {
    const score = question.answer?.answer_value;
    const requiresDataCapture =
      score !== null && score !== undefined && Number(score) < 50;
    setShowDataCaptureBox(requiresDataCapture);
  }, [question.answer?.answer_value]); // Only depends on the score prop

  // --- Effect to Sync Prop Changes TO Local State ---
  useEffect(() => {
    const propDataArray = question.answer?.data_capture_values;
    // Define the shape of the data from props (or default empty)
    const propData =
      Array.isArray(propDataArray) && propDataArray.length > 0
        ? propDataArray[0]
        : { fieldName: "", from: "", to: "" };

    // Compare prop data with local state, update local state if props differ.
    // This handles initial load and external updates without overwriting user input.
    if (
      propData.fieldName !== localDataCaptureFields.fieldName ||
      propData.from !== localDataCaptureFields.from ||
      propData.to !== localDataCaptureFields.to
    ) {
      console.log(
        `DataCapture ${question.id}: Prop data differs, syncing local state.`,
      );
      setLocalDataCaptureFields({ ...propData }); // Update with a clone
    }
    // This effect should ONLY run when the source prop changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.answer?.data_capture_values]); // Dependency: the source data array

  // --- Handler for LOCAL Data Capture Field Changes ---
  const handleLocalDataCaptureChange = (e) => {
    const { name, value } = e.target;
    setLocalDataCaptureFields((prev) => ({ ...prev, [name]: value })); // Update local state only
  };

  // --- Function to Update GLOBAL State on Blur ---
  const syncGlobalDataCapture = useCallback(() => {
    // Use setTimeout to defer the execution slightly
    setTimeout(() => {
      const score = question.answer?.answer_value; // Score from global state
      const requiresDataCapture = score !== null && Number(score) < 50;

      // Get the current global data capture values for comparison
      const currentGlobalDataArray = question.answer?.data_capture_values;
      const currentGlobalData =
        Array.isArray(currentGlobalDataArray) &&
        currentGlobalDataArray.length > 0
          ? currentGlobalDataArray[0]
          : { fieldName: "", from: "", to: "" };

      // Only update global state if local fields differ from current global state
      if (
        localDataCaptureFields.fieldName !== currentGlobalData.fieldName ||
        localDataCaptureFields.from !== currentGlobalData.from ||
        localDataCaptureFields.to !== currentGlobalData.to
      ) {
        console.log(
          `DataCapture ${question.id}: (Deferred) Syncing global data capture on blur:`,
          localDataCaptureFields,
        );
        handleSetValues((prevAllQuestions) =>
          prevAllQuestions.map((group) => ({
            ...group,
            question: group.question.map((q) => {
              if (q.id === question.id) {
                // Recalculate answered status based on score and NEW data capture state
                const isDataCaptureComplete =
                  localDataCaptureFields.fieldName?.trim() !== "" &&
                  localDataCaptureFields.from?.trim() !== "" &&
                  localDataCaptureFields.to?.trim() !== "";
                const isAnswered =
                  q.answer.answer_value !== null && // Score must exist
                  (!requiresDataCapture || isDataCaptureComplete); // And if required, data capture must be complete

                return {
                  ...q,
                  answer: {
                    ...q.answer,
                    // Store the updated local data if capture is required
                    data_capture_values: requiresDataCapture
                      ? [{ ...localDataCaptureFields }] // Store a clone
                      : null,
                    answered: isAnswered, // Update answered status
                  },
                };
              }
              return q;
            }),
          })),
        );
      } else {
        console.log(
          `DataCapture ${question.id}: (Deferred) Blur detected, but local data matches prop. No global update needed.`,
        );
      }
    }, 0); // Set timeout with 0 delay - pushes execution after current browser tasks (like focus shift)
  }, [
    localDataCaptureFields,
    question.id,
    question.answer?.answer_value,
    question.answer?.data_capture_values,
    handleSetValues,
  ]); // Dependencies remain the same

  // --- Update GLOBAL state when radio button changes ---
  const handleRadioChange = useCallback(
    (e) => {
      const newScore = e.target.value;
      const newRequiresDataCapture = Number(newScore) < 50;
      const selectedAnswerOption = (question.answers || []).find(
        (ans) => ans.value === newScore,
      );

      handleSetValues((prevAllQuestions) =>
        prevAllQuestions.map((group) => ({
          ...group,
          question: group.question.map((q) => {
            if (q.id === question.id) {
              // Check data capture completeness based on LOCAL state, as that's what *would* be saved if required
              const isDataCaptureComplete =
                localDataCaptureFields.fieldName?.trim() !== "" &&
                localDataCaptureFields.from?.trim() !== "" &&
                localDataCaptureFields.to?.trim() !== "";

              // Determine NEW answered status based on NEW score and LOCAL data capture state
              const isAnswered =
                newScore !== null &&
                (!newRequiresDataCapture || isDataCaptureComplete);

              return {
                ...q,
                answer: {
                  ...q.answer,
                  answer_value: newScore, // Update score
                  // Ensure data_capture_values array exists if required, otherwise null.
                  // Keep existing content if switching to required, using LOCAL state as the base
                  data_capture_values: newRequiresDataCapture
                    ? [{ ...localDataCaptureFields }]
                    : null,
                  answered: isAnswered, // Update answered status
                  auto_fail: selectedAnswerOption?.failEvaluation || false,
                  sendEmail: selectedAnswerOption?.sendEmail || false,
                },
              };
            }
            return q;
          }),
        })),
      );
    },
    [question.id, question.answers, handleSetValues, localDataCaptureFields], // Depends on local fields now
  );

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ pt: 1.5, pb: "12px !important" }}>
        <Stack spacing={1}>
          {/* Question Label & Info */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            mb={0.5}
          >
            {/* ... Label/Tooltip */}
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <FormLabel
                id={`label-${question.id}`}
                sx={{ fontWeight: 500, fontSize: "0.95rem" }}
              >
                {question?.name}
              </FormLabel>
              {question.description && (
                <Tooltip title={question.description} placement="top">
                  <Info
                    color="action"
                    sx={{
                      fontSize: "1rem",
                      cursor: "help",
                      verticalAlign: "middle",
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
            {/* Answered icon reflects GLOBAL state */}
            {question.answer.answered && (
              <DoneAllIcon color="success" fontSize="small" />
            )}
          </Stack>

          {/* Radio Buttons */}
          <FormControl component="fieldset">
            <RadioGroup
              onChange={handleRadioChange} // Uses updated handler
              aria-labelledby={`label-${question.id}`}
              value={question.answer.answer_value ?? ""} // Reads global state for score
              name={`radio-${question.id}`}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              {(question.answers || []).map((answer) => (
                <FormControlLabel
                  key={answer.id}
                  value={answer.value}
                  control={<Radio size="small" />}
                  label={answer.answer}
                  sx={{ mb: -0.5 }}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {/* Data Capture Box (Conditional based on derived state) */}
          {showDataCaptureBox && (
            <Box
              sx={{
                border: "1px dashed #ccc",
                p: 1.5,
                mt: 1.5,
                borderRadius: 1,
              }}
            >
              <Typography
                variant="caption"
                display="block"
                gutterBottom
                sx={{ color: "text.secondary", mb: 1 }}
              >
                Correction Required: Specify the error found.
              </Typography>
              <Grid container spacing={1} alignItems="flex-start">
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Field Name with Error"
                    name="fieldName"
                    value={localDataCaptureFields.fieldName} // Bind to local state
                    onChange={handleLocalDataCaptureChange} // Update local state
                    onBlur={syncGlobalDataCapture} // Sync global on blur
                    margin="dense"
                    size="small"
                    helperText="e.g., Customer Name"
                  />
                </Grid>
                <Grid item xs={12} sm={5.5}>
                  <TextField
                    fullWidth
                    label="Incorrect Value Found"
                    name="from"
                    value={localDataCaptureFields.from} // Bind to local state
                    onChange={handleLocalDataCaptureChange} // Update local state
                    onBlur={syncGlobalDataCapture} // Sync global on blur
                    margin="dense"
                    size="small"
                    helperText="e.g., Jon"
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={1}
                  sx={{ textAlign: "center", pt: { sm: 3.5 } }}
                >
                  <ArrowForwardIcon color="action" fontSize="small" />
                </Grid>
                <Grid item xs={12} sm={5.5}>
                  <TextField
                    fullWidth
                    label="Correct Value Required"
                    name="to"
                    value={localDataCaptureFields.to} // Bind to local state
                    onChange={handleLocalDataCaptureChange} // Update local state
                    onBlur={syncGlobalDataCapture} // Sync global on blur
                    margin="dense"
                    size="small"
                    helperText="e.g., John"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
});
